import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function ForcePasswordChange({ onDone }) {
  const { logout } = useAuth();
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.newPassword.length < 6) return toast.error("New password must be at least 6 characters.");
    if (form.newPassword !== form.confirm) return toast.error("New passwords do not match.");
    if (form.newPassword === form.currentPassword) return toast.error("New password must be different from the current one.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      toast.success("Password updated. Welcome!");
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400";

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4" data-testid="force-password-change">
      <form onSubmit={submit} className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-7 shadow-sm space-y-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
            <ShieldAlert className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-heading font-semibold text-slate-900 text-lg">Set a new password</h1>
            <p className="text-xs text-slate-500">For security, you must change your default password before continuing.</p>
          </div>
        </div>
        <div className="space-y-2.5">
          <input
            data-testid="force-current-password"
            type="password"
            className={inputCls}
            placeholder="Current password"
            autoComplete="current-password"
            value={form.currentPassword}
            onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
          />
          <input
            data-testid="force-new-password"
            type="password"
            className={inputCls}
            placeholder="New password (min 6 characters)"
            autoComplete="new-password"
            value={form.newPassword}
            onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
          />
          <input
            data-testid="force-confirm-password"
            type="password"
            className={inputCls}
            placeholder="Confirm new password"
            autoComplete="new-password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          />
        </div>
        <button
          data-testid="force-submit-btn"
          type="submit"
          disabled={busy}
          className="w-full bg-slate-900 text-white rounded-md px-4 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:opacity-60 transition-colors"
        >
          {busy ? "Saving…" : "Update password & continue"}
        </button>
        <button
          type="button"
          onClick={logout}
          className="w-full text-xs text-slate-400 hover:text-slate-600"
          data-testid="force-logout-btn"
        >
          Sign out
        </button>
      </form>
    </div>
  );
}
